import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { Input } from '../ui/input';
import { toast } from 'sonner';

/** Ridimensiona e comprime l'immagine in WebP (max 900px, qualità 65%).
 *  WebP è ~30-50% più piccolo di JPEG a parità di qualità visiva.
 *  PNG con trasparenza → rimane PNG a 900px. */
function compressImage(file, maxPx = 900, quality = 0.65) {
  const isPng = file.type === 'image/png';
  // Controlla se il browser supporta WebP in output (Safari < 16 non lo fa)
  const supportsWebP = (() => {
    try {
      const c = document.createElement('canvas');
      c.width = 1; c.height = 1;
      return c.toDataURL('image/webp').startsWith('data:image/webp');
    } catch { return false; }
  })();
  const outputType = isPng ? 'image/png' : (supportsWebP ? 'image/webp' : 'image/jpeg');
  return new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width >= height) {
          height = Math.round((height * maxPx) / width);
          width = maxPx;
        } else {
          width = Math.round((width * maxPx) / height);
          height = maxPx;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!isPng) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
      }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL(outputType, isPng ? undefined : quality));
    };
    img.onerror = reject;
    img.src = blobUrl;
  });
}

/**
 * Campo immagine che supporta sia URL esterni sia upload da file locale.
 * I file caricati vengono compressi (Canvas → JPEG 82%, max 1920px) prima
 * di essere convertiti in base64 e salvati nel DB.
 *
 * Props:
 *  - value        {string}   URL corrente (http o data:image/...)
 *  - onChange     {fn}       Callback con il nuovo valore stringa
 *  - placeholder  {string}   Testo placeholder dell'input URL
 *  - previewClass {string}   Classi Tailwind per l'anteprima (default "h-24")
 *  - maxSizeMB    {number}   Dimensione massima file ORIGINALE in MB (default 15)
 */
/** Restituisce false per gli URL CDN Instagram hotlink-protetti. */
function isSafePreview(url) {
  if (!url) return false;
  if (url.startsWith('data:')) return true;
  if (url.startsWith('/api/image/')) return true;  // endpoint WebP relativo
  if (url.includes('/api/image/')) return true;    // endpoint WebP assoluto
  if (url.includes('cdninstagram.com') || url.includes('scontent-')) return false;
  return url.startsWith('http');
}

export default function ImageUploadField({
  value,
  onChange,
  placeholder = 'https://... oppure carica da PC',
  previewClass = 'h-24',
  maxSizeMB = 15,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Seleziona un file immagine valido (jpg, png, webp…)');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Immagine troppo grande. Massimo ${maxSizeMB} MB`);
      return;
    }

    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      onChange(dataUrl);
    } catch {
      // Fallback senza compressione (es. SVG o formato non supportato da Canvas)
      const reader = new FileReader();
      reader.onload = (ev) => { onChange(ev.target.result); };
      reader.onerror = () => toast.error('Errore nella lettura del file');
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="rounded-xl flex-1 font-body text-sm"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-body transition-colors flex-shrink-0 border border-gray-200 disabled:opacity-50 whitespace-nowrap"
        >
          <Upload className="w-4 h-4" />
          {uploading ? 'Compressione…' : 'Carica'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      {isSafePreview(value) && (
        <img
          src={value}
          alt="anteprima"
          className={`mt-2 ${previewClass} w-full object-cover rounded-lg`}
        />
      )}
    </div>
  );
}
